// The original's front-end art on the HTML pages (home page, lobby; docs/platform/ClientShell.md
// "Lobby style"): the front end's fonts as CSS families, its sprites as CSS custom properties and
// the themes' and forts' front-end resources.
//
// - Fonts: the UI font files (`QtShell_UI::Initialise`'s AddFont files, assets/ui/fonts.json) as
//   the families `WMD Blitzkrieg` (titles and buttons, as the lobby's titles: font 1003),
//   `WMD Serif` (bold: names and labels, font 1021) and `WMD Ornitons` (values, font 1030).
// - Sprites: the ui_textures sprites of `SPRITES` (and the forts' preview images) cut out of their
//   atlas pages by ip2's decoder worker (no main-thread decode of a 2048x2048 page), each made a
//   PNG blob and set as `--art-<file name>` on the document element (`url(blob:…)`), e.g.
//   `--art-fe-lobbybutton-01`. `html.fe-art` marks that every sprite is there: the CSS draws its
//   own gradients until then (and when the assets are missing).
// - Themes (Scripts/Themes.txt): a theme's `FrontendResource` (the front end's animated backdrop,
//   render/backdrop.ts); forts (Scripts/Forts.txt): a fort's `PreviewImage`.
import { assetJson, assetUrl } from '../assets.ts';
import { decodePixels } from './ip2/ip2.ts';

/** The pages' fonts: family, file, weight. */
const FONTS: [string, string, string][] = [
  ['WMD Blitzkrieg', 'ui/fonts/blitzkrieg nf.ttf', 'normal'],
  ['WMD Serif', 'ui/fonts/wmd-serifbold.ttf', 'bold'],
  ['WMD Serif', 'ui/fonts/wmd-serifregular.ttf', 'normal'],
  ['WMD Ornitons', 'ui/fonts/ornitonsserial-regular.ttf', 'normal'],
];

/** The sprites the pages use (ui_textures file names), and the standalone logo's ink rectangle. */
const SPRITES = [
  // Panels and boxes (wmd_onlinelobby: TEX_Backer, TEX_ListBacker, TEX_BackingPanel_01, the
  // header strips, the global chat's boxes; the dialogs' panel).
  'fe_backingbox_05', 'fe_backingbox_09', 'hu_bigpaneldark_01', 'fe_whiteheaderbox_03', 'fe_dialoguepanel_02',
  'fe_globalchat_headerbox_01', 'fe_globalchat_backingbox_03', 'fe_globalchat_textbox_01',
  // Buttons (style guide BUTTON Dialog and AddPlayer: NotFocused _01, Focused and Pressed _03).
  'fe_lobbybutton_01', 'fe_lobbybutton_03', 'fe_lobbyaddbutton_01', 'fe_lobbyaddbutton_03',
  // The slots: the player button, avatars, AI difficulty, ready lights, host crown, speech bank.
  'fe_lobbyplayerbutton_01', 'hu_playerpic_01', 'hu_playeravatar_01', 'hu_playeravatar_02', 'hu_playeravatar_03',
  'hu_playeravatar_04', 'hu_playeravatar_05', 'hu_playeravatar_06', 'fe_aidifficulty_01', 'fe_aidifficulty_02',
  'fe_aidifficulty_03', 'fe_aidifficulty_04', 'fe_aidifficulty_05', 'fe_readylight_01', 'fe_readylight_02',
  'hu_crowntiny_01', 'fe_chat_01',
  // Controls: the drop-down arrow, the host-only padlock (style guide TICKBOX Locked), the
  // landscape thumbnail's highlight (wmd_locallobby TEX_LandHighlight).
  'fe_dropdownarrow_01', 'fe_padlock_01', 'fe_lobbylandgenhighlight_01',
  // The home page: the small logo and the multiplayer menu's host and browse tiles.
  'fe_tinyswitchwmdlogo_01', 'fe_match_hostgame_01', 'fe_match_browsegame_01',
];
const LOGO = { file: 'wormswmd_logo', rect: [10, 191, 994, 690] as [number, number, number, number] };

type Sprites = {
  sprites: Record<string, { page: string; rect: [number, number, number, number] }>;
  pages: Record<string, { png?: string }>;
  standalone: Record<string, { png?: string }>;
};
type Datum = { name: string; value?: string; children?: Datum[] };

/** `--art-<name>`: the CSS custom property of a ui_textures file (any case, with or without its
 * extension). */
export function artVar(file: string): string {
  return `--art-${file.toLowerCase().replace(/\.png$/, '').replace(/[^a-z0-9]+/g, '-')}`;
}

/** The groups of a script's Datum tree (assets/data/scripts/json): group name -> field -> value. */
async function scriptGroups(path: string): Promise<Map<string, Map<string, string>>> {
  const doc = await assetJson<{ root: Datum }>(`data/scripts/json/CommonData/Scripts/${path}.json`);
  return new Map((doc.root.children ?? []).map(g => [g.name, new Map((g.children ?? []).map(f => [f.name, f.value ?? '']))]));
}

let themes: Promise<Map<string, Map<string, string>>> | null = null;

/** The `FrontendResource` (the animated backdrop's xDesc file) of theme `name`, or null. */
export async function themeBackdrop(name: string): Promise<string | null> {
  themes ??= scriptGroups('Themes.txt');
  return (await themes).get(name)?.get('FrontendResource') ?? null;
}

/** The front end's music and ambience of theme `name` (`ThemeMan::GetFrontendResourceInfo`
 * 0xc4a1d0: `FrontendMusic`, `FrontendAmbient`; `ChangeBackgroundMusic` plays them). */
export async function themeFrontendAudio(name: string): Promise<{ music: string | null; ambience: string | null }> {
  themes ??= scriptGroups('Themes.txt');
  const t = (await themes).get(name);
  return { music: t?.get('FrontendMusic') ?? null, ambience: t?.get('FrontendAmbient') ?? null };
}

/** Every theme with a front-end backdrop (the home page picks one). */
export async function backdropThemes(): Promise<string[]> {
  themes ??= scriptGroups('Themes.txt');
  return [...(await themes)].filter(([, f]) => f.get('Type') === 'Theme' && f.get('FrontendResource')).map(([n]) => n);
}

/** Fort UI name (`Gui.Forts.Castle`) -> its `PreviewImage` file (null until loaded). */
let fortPreviews: Map<string, string> | null = null;

/** The CSS custom property of fort `name`'s preview image, or null. */
export function fortPreviewVar(name: string): string | null {
  const file = fortPreviews?.get(name);
  return file ? artVar(file) : null;
}

/** A sprite's pixels as a PNG blob. */
function pngBlob(px: { w: number; h: number; data: Uint8ClampedArray<ArrayBuffer> }): Promise<Blob | null> {
  const c = document.createElement('canvas');
  c.width = px.w;
  c.height = px.h;
  c.getContext('2d')?.putImageData(new ImageData(px.data, px.w, px.h), 0, 0);
  const { promise, resolve } = Promise.withResolvers<Blob | null>();
  c.toBlob(resolve, 'image/png');
  return promise;
}

let loading: Promise<void> | null = null;

/** Registers the fonts and makes the sprites' properties (once); resolves when every sprite that
 * decoded is set. */
export function loadArt(): Promise<void> {
  loading ??= (async () => {
    for (const [family, file, weight] of FONTS) {
      const face = new FontFace(family, `url("${assetUrl(file)}")`, { weight, display: 'swap' });
      document.fonts.add(face);
      void face.load().catch(e => console.warn('font', file, e));
    }
    const [sp, forts] = await Promise.all([assetJson<Sprites>('ui/sprites.json'), scriptGroups('Forts.txt')]);
    fortPreviews = new Map([...forts.values()].flatMap(f => (f.get('Name') && f.get('PreviewImage') ? [[f.get('Name')!, f.get('PreviewImage')!]] : [])));
    // The sprites by page (one decode per page).
    const jobs = new Map<string, { file: string; rect: [number, number, number, number] | null }[]>();
    const want = (png: string | undefined, file: string, rect: [number, number, number, number] | null): void => {
      if (!png) return;
      const q = jobs.get(png) ?? [];
      q.push({ file, rect });
      jobs.set(png, q);
    };
    for (const file of [...SPRITES, ...[...fortPreviews.values()].map(f => f.toLowerCase().replace(/\.png$/, ''))]) {
      const s = sp.sprites[`uidata/ui_textures/${file}.png`];
      if (s) want(sp.pages[s.page]?.png, file, s.rect);
      else console.warn('front-end art: no sprite', file);
    }
    want(sp.standalone[`uidata/ui_textures/${LOGO.file}.png`]?.png, LOGO.file, LOGO.rect);
    const root = document.documentElement.style;
    await Promise.all([...jobs].map(async ([png, q]) => {
      const pixels = await decodePixels(png, q.map(x => x.rect));
      await Promise.all(pixels.map(async (px, i) => {
        const blob = px ? await pngBlob(px) : null;
        if (blob) root.setProperty(artVar(q[i].file), `url("${URL.createObjectURL(blob)}")`);
      }));
    }));
    document.documentElement.classList.add('fe-art');
  })().catch(e => console.warn('front-end art', e));
  return loading;
}
